import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { InspoImportSaveSummary } from '@/components/InspoImport/inspoImportTypes';
import type { TripImportEntry } from '@/models/itineraryImportTypes';

export interface OpenTripImportOptions {
  prefilledText?: string;
  entry?: TripImportEntry;
  /** Called after a list/trip is saved; the modal closes instead of showing its success screen. */
  onCompleteSave?: (summary: InspoImportSaveSummary) => void;
}

interface TripImportContextValue {
  isOpen: boolean;
  prefilledText: string;
  entry: TripImportEntry;
  onCompleteSave: ((summary: InspoImportSaveSummary) => void) | null;
  openTripImport: (options?: OpenTripImportOptions) => void;
  closeTripImport: () => void;
  clearPrefilledText: () => void;
}

const TripImportContext = createContext<TripImportContextValue | null>(null);

export function useTripImport(): TripImportContextValue {
  const ctx = useContext(TripImportContext);
  if (!ctx) {
    throw new Error('useTripImport must be used within TripImportProvider');
  }
  return ctx;
}

export function TripImportProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [entry, setEntry] = useState<TripImportEntry>('create_flyout');
  const [onCompleteSave, setOnCompleteSave] = useState<
    ((summary: InspoImportSaveSummary) => void) | null
  >(null);

  const openTripImport = useCallback((options: OpenTripImportOptions = {}) => {
    setPrefilledText(options.prefilledText?.trim() ?? '');
    setEntry(options.entry ?? 'create_flyout');
    setOnCompleteSave(() => options.onCompleteSave ?? null);
    setIsOpen(true);
  }, []);

  const closeTripImport = useCallback(() => {
    setIsOpen(false);
    setPrefilledText('');
    setEntry('create_flyout');
    setOnCompleteSave(null);
  }, []);

  const clearPrefilledText = useCallback(() => {
    setPrefilledText('');
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      prefilledText,
      entry,
      onCompleteSave,
      openTripImport,
      closeTripImport,
      clearPrefilledText,
    }),
    [
      isOpen,
      prefilledText,
      entry,
      onCompleteSave,
      openTripImport,
      closeTripImport,
      clearPrefilledText,
    ],
  );

  return <TripImportContext.Provider value={value}>{children}</TripImportContext.Provider>;
}
